import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { RefreshCw, Clock, CheckCircle, XCircle, Loader2, Play, History, Settings2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const INTERVAL_OPTIONS = [
  { value: '2', label: 'Every 2 hours' },
  { value: '4', label: 'Every 4 hours' },
  { value: '6', label: 'Every 6 hours' },
  { value: '12', label: 'Every 12 hours' },
  { value: '24', label: 'Every 24 hours' },
];

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function StatusBadge({ status }) {
  const map = {
    success: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
    failed: { cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
    running: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2 },
    skipped: { cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  };
  const m = map[status] || map.skipped;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`text-[10px] ${m.cls}`} data-testid={`sync-status-${status}`}>
      <Icon className={`w-3 h-3 mr-1 ${status === 'running' ? 'animate-spin' : ''}`} />{status}
    </Badge>
  );
}

export default function SyncSettingsPage() {
  const [settings, setSettings] = useState({ enabled: false, interval_hours: 24 });
  const [nextRun, setNextRun] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get('/training/sync/settings'),
      api.get('/training/sync/history'),
    ]).then(([sR, hR]) => {
      if (sR.status === 'fulfilled') {
        setSettings(sR.value.data.settings || { enabled: false, interval_hours: 24 });
        setNextRun(sR.value.data.next_run_time);
        setIsRunning(sR.value.data.is_running);
      }
      if (hR.status === 'fulfilled') {
        setHistory(hR.value.data.logs || []);
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll when a sync is running
  useEffect(() => {
    if (!isRunning) return;
    const iv = setInterval(() => {
      api.get('/training/sync/settings').then(r => {
        setIsRunning(r.data.is_running);
        setNextRun(r.data.next_run_time);
        if (!r.data.is_running) {
          fetchAll();
        }
      });
    }, 5000);
    return () => clearInterval(iv);
  }, [isRunning, fetchAll]);

  const handleSave = async (enabled, interval_hours) => {
    setSaving(true);
    try {
      const res = await api.put('/training/sync/settings', { enabled, interval_hours });
      setSettings(res.data.settings);
      setNextRun(res.data.next_run_time);
      toast.success(enabled ? `Auto-sync enabled every ${interval_hours}h` : 'Auto-sync disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await api.post('/training/sync/trigger');
      toast.success('Full sync started (Books → CRM → Map Students)');
      setIsRunning(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger sync');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="sync-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Sync Settings</h1>
        <p className="text-sm text-slate-400 mt-0.5">Configure automatic Zoho synchronization</p>
      </div>

      {/* Auto-Sync Configuration */}
      <Card className="border p-6" data-testid="sync-config-card">
        <div className="flex items-center gap-2 mb-5">
          <Settings2 className="w-4 h-4 text-violet-500" />
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Auto-Sync Configuration</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-sm bg-slate-50/50">
            <div>
              <p className="text-sm font-medium text-slate-800">Auto-Sync</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Runs Books → CRM → Map Students</p>
            </div>
            <Switch
              data-testid="sync-enabled-toggle"
              checked={settings.enabled}
              disabled={saving}
              onCheckedChange={(checked) => handleSave(checked, settings.interval_hours)}
            />
          </div>

          {/* Interval Selector */}
          <div className="p-4 border rounded-sm bg-slate-50/50">
            <p className="text-sm font-medium text-slate-800 mb-2">Sync Interval</p>
            <Select
              value={String(settings.interval_hours)}
              onValueChange={(val) => handleSave(settings.enabled, parseInt(val))}
              disabled={saving}
            >
              <SelectTrigger className="w-full" data-testid="sync-interval-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERVAL_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="p-4 border rounded-sm bg-slate-50/50">
            <p className="text-sm font-medium text-slate-800 mb-2">Status</p>
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                <span className="text-sm text-blue-600 font-medium">Sync in progress...</span>
              </div>
            ) : settings.enabled && nextRun ? (
              <div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-xs text-slate-500">Next run:</span>
                </div>
                <p className="text-sm font-medium text-slate-800 mt-1" data-testid="sync-next-run">{fmtDate(nextRun)}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-400">Disabled</p>
            )}
          </div>
        </div>

        {/* Manual Trigger */}
        <div className="mt-5 pt-5 border-t flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Manual Sync</p>
            <p className="text-[10px] text-slate-400">Run a full sync now (Books → CRM → Map Students)</p>
          </div>
          <button
            onClick={handleTrigger}
            disabled={triggering || isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-sm hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="sync-trigger-btn"
          >
            {triggering || isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {isRunning ? 'Running...' : 'Run Full Sync'}
          </button>
        </div>
      </Card>

      {/* Sync History */}
      <Card className="border p-0" data-testid="sync-history-card">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-violet-500" />
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Sync History</p>
          </div>
          <button onClick={fetchAll} className="text-xs text-violet-600 hover:underline flex items-center gap-1" data-testid="sync-refresh-history">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {history.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[9px] uppercase">Type</TableHead>
                <TableHead className="text-[9px] uppercase">Status</TableHead>
                <TableHead className="text-[9px] uppercase">Started</TableHead>
                <TableHead className="text-[9px] uppercase">Completed</TableHead>
                <TableHead className="text-[9px] uppercase">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((log) => (
                <TableRow
                  key={log.id}
                  className="cursor-pointer hover:bg-violet-50/30"
                  onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  data-testid={`sync-history-row-${log.id}`}
                >
                  <TableCell>
                    <Badge variant="outline" className="text-[9px]">
                      <Zap className="w-3 h-3 mr-1" />{log.type || 'sync'}
                    </Badge>
                  </TableCell>
                  <TableCell><StatusBadge status={log.status} /></TableCell>
                  <TableCell className="text-[10px] text-slate-500">{fmtDate(log.started_at)}</TableCell>
                  <TableCell className="text-[10px] text-slate-500">{fmtDate(log.completed_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {log.type === 'auto_sync' && log.steps?.length > 0
                      ? `${log.steps.length} steps`
                      : log.unique_students ? `${log.unique_students} students`
                      : log.total_batches ? `${log.total_batches} batches`
                      : log.enrollments_created != null ? `${log.enrollments_created} enrollments`
                      : log.error ? log.error.substring(0, 40) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-xs text-slate-400 text-center py-8">No sync history yet</p>
        )}

        {/* Expanded Steps */}
        {expandedLog && history.find(h => h.id === expandedLog)?.steps?.length > 0 && (
          <div className="border-t px-5 py-3 bg-slate-50/50" data-testid="sync-steps-panel">
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Sync Steps</p>
            <div className="space-y-1">
              {history.find(h => h.id === expandedLog).steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <span className="text-slate-400 whitespace-nowrap">{new Date(s.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  <span className="text-slate-600">{s.msg}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
